import { IGeneralHistory, IGroupedHistory } from "../redux/common/common.types";

const HISTORY_GROUP_WINDOW_MS = 60 * 1000; // 1 minute

const withinWindow = (a: IGeneralHistory, b: IGeneralHistory): boolean => {
  const ta = new Date(a.create_ts).getTime();
  const tb = new Date(b.create_ts).getTime();
  if (Number.isNaN(ta) || Number.isNaN(tb)) return false;
  return Math.abs(ta - tb) <= HISTORY_GROUP_WINDOW_MS;
};

// Merge rule: exact (non-empty) remark match + create_ts within 1 minute of the
// adjacent record. Chaining is adjacent — each record is compared against the
// last member already in the group, so a continuous burst of edits collapses
// into a single group even if its first and last members are >60s apart.
const isSameGroup = (prev: IGeneralHistory, curr: IGeneralHistory): boolean => {
  const remarkA = prev.remark ?? "";
  const remarkB = curr.remark ?? "";
  if (remarkA === "" || remarkA !== remarkB) return false;
  return withinWindow(prev, curr);
};

/**
 * Groups a newest-first history list into logical actions. Adjacent records
 * with the same remark and create_ts within 60s are merged into one group.
 * The shared remark is exposed once as `commonRemark`.
 */
export const groupHistoryRecords = (
  list: IGeneralHistory[],
): IGroupedHistory[] => {
  const groups: IGroupedHistory[] = [];

  (list || []).forEach((item) => {
    const last = groups[groups.length - 1];
    const anchor = last?.items[last.items.length - 1];

    if (last && anchor && isSameGroup(anchor, item)) {
      last.items.push(item);
    } else {
      groups.push({
        groupId: item.history_uuid || String(item.history_id ?? groups.length),
        commonRemark: item.remark ?? null,
        latestTs: item.create_ts,
        items: [item],
      });
    }
  });

  return groups;
};

export const sanitizeHistoryMessage = (message: string) => {
  if (!message) return "";

  // Split sentence into "prefix" and list part
  const match = message.match(/^(.*?has updated\s*)(.*)$/i);
  if (!match) return message.trim();

  const [, prefix, rawList] = match;

  // Remove unwanted field names
  const cleanedList = rawList
    .replace(/company outlet name/gi, "")
    .replace(/modified by name/gi, "")
    .replace(/created by name/gi, "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  return `${prefix}${cleanedList.join(", ")}`.trim();
};
